/**
 * @fileoverview SessionViewerProvider Component
 * 
 * Top-level provider that manages all SessionViewer instances using react-reverse-portal
 * for efficient reparenting without remounting.
 * 
 * **Architecture Overview:**
 * 
 * This provider creates portal nodes for each session using react-reverse-portal.
 * SessionViewer components are rendered once into InPortals and can be displayed
 * anywhere using OutPortals without remounting or losing state.
 * 
 * **Why React-Reverse-Portal:**
 * 
 * - React-native solution (works WITH React, not against it)
 * - No DOM/React reconciliation conflicts
 * - Preserves component state and LiveKit connections
 * - Eliminates manual DOM manipulation and race conditions
 * - Simpler, more maintainable code
 * 
 * **Performance Optimization:**
 * 
 * This provider only subscribes to `focusedSessionSid` from Zustand, not the
 * entire `sessions` object. This prevents unnecessary re-renders when session
 * properties (like noise, handRaised, etc.) update. The SessionViewerPool
 * component handles the sessions subscription separately.
 * 
 * @module vtc/contexts/SessionViewerContext/SessionViewerProvider
 */

import React, { useRef, useEffect } from 'react';
import * as portals from 'react-reverse-portal';
import { SessionViewerContext } from './SessionViewerContext';
import { SessionViewerPool } from './SessionViewerPool';
import { getEnabledComponentsForSession } from './utils';
import useSessionStore from '../../stores/session';

/**
 * SessionViewerProvider Component
 * 
 * Renders all SessionViewer instances in a hidden container and provides access
 * to their DOM elements for portal-based rendering.
 * 
 * **Important:** Only subscribes to focusedSessionSid to avoid unnecessary re-renders.
 * The SessionViewerPool component handles sessions subscriptions separately.
 * 
 * **Usage:**
 * ```jsx
 * <SessionViewerProvider>
 *   <YourApp />
 * </SessionViewerProvider>
 * ```
 * 
 * Then use SessionViewerPortal to display viewers:
 * ```jsx
 * <SessionViewerPortal sessionSid="session_123" isFocused={true} />
 * ```
 * 
 * @component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element}
 * 
 * @example
 * // Wrap your app with the provider
 * function App() {
 *   return (
 *     <SessionViewerProvider>
 *       <SessionGrid />
 *       <FocusView />
 *     </SessionViewerProvider>
 *   );
 * }
 */
export function SessionViewerProvider({ children }) {
    // Only subscribe to focusedSessionSid to minimize re-renders
    // Use getState() to access sessions object without subscribing to it
    const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);

    // Store portal nodes for each session (created once, reused forever)
    const portalNodesRef = useRef({});

    // Track which sessions have had their default tab set
    const initializedTabsRef = useRef(new Set());

    /**
     * Get or create a portal node for a session
     * Portal nodes are created once and reused for the lifetime of the session
     * 
     * @param {string} sessionSid - Session ID
     * @returns {PortalNode} Portal node for the session
     */
    const getOrCreatePortalNode = (sessionSid) => {
        if (!portalNodesRef.current[sessionSid]) {
            portalNodesRef.current[sessionSid] = portals.createHtmlPortalNode({
                attributes: {
                    style: 'width: 100%; height: 100%; display: flex; flex-direction: column;'
                }
            });
            console.debug(`[SessionViewerProvider] Created portal node for ${sessionSid}`);
        }
        return portalNodesRef.current[sessionSid];
    };

    /**
     * Remove a session's portal node (called when session is removed from store)
     * 
     * @param {string} sessionSid - Session ID to remove
     */
    const removeSessionViewer = (sessionSid) => {
        if (portalNodesRef.current[sessionSid]) {
            delete portalNodesRef.current[sessionSid];
            console.debug(`[SessionViewerProvider] Removed portal node for ${sessionSid}`);
        }

        // Clean up initialized tabs tracking
        initializedTabsRef.current.delete(sessionSid);
    };

    // Set removeSessionViewer reference in session store on mount
    useEffect(() => {
        const removeSessionViewerRefObject = { current: removeSessionViewer };
        useSessionStore.setState({ removeSessionViewerRef: removeSessionViewerRefObject });

        return () => {
            useSessionStore.setState({ removeSessionViewerRef: null });
        };
    }, []);

    // Update enabled components when focused session changes OR on initial mount
    // This ensures components are enabled even if a session is already focused on page load
    useEffect(() => {
        if (!focusedSessionSid) return;

        // Use getState() to avoid subscribing to sessions
        const sessions = useSessionStore.getState().sessions;
        const session = sessions[focusedSessionSid];
        const viewerRef = session?.viewerRef;

        // Wait for viewerRef to be available (may not be ready on initial mount)
        if (!viewerRef?.current) {
            // Retry after a short delay to allow SessionViewer to mount
            const timer = setTimeout(() => {
                const updatedSessions = useSessionStore.getState().sessions;
                const updatedViewerRef = updatedSessions[focusedSessionSid]?.viewerRef;

                if (updatedViewerRef?.current?.enableComponents) {
                    const enabledComponents = getEnabledComponentsForSession(focusedSessionSid);
                    //updatedViewerRef.current.enableComponents(enabledComponents);

                    // Only set the default tab on the FIRST focus of this session
                    if (!initializedTabsRef.current.has(focusedSessionSid)) {
                        if (enabledComponents.sessionDetails?.defaultTab && updatedViewerRef.current.setSessionDetailsTab) {
                            //updatedViewerRef.current.setSessionDetailsTab(enabledComponents.sessionDetails.defaultTab);
                            initializedTabsRef.current.add(focusedSessionSid);
                        }
                    }
                }
            }, 100);

            return () => clearTimeout(timer);
        }

        // NOTE: Removed enableComponents call here because SessionViewerPool already passes
        // enabledComponents as props, and Session.jsx sets them once on initialization.
        // Calling enableComponents here was causing redundant updates and race conditions.

        // Only set the default tab on the FIRST focus of this session
        // Don't override user's tab selection on subsequent focuses
        if (viewerRef.current.setSessionDetailsTab && !initializedTabsRef.current.has(focusedSessionSid)) {
            const enabledComponents = getEnabledComponentsForSession(focusedSessionSid);
            if (enabledComponents.sessionDetails?.defaultTab) {
                //viewerRef.current.setSessionDetailsTab(enabledComponents.sessionDetails.defaultTab);
                initializedTabsRef.current.add(focusedSessionSid);
            }
        }
    }, [focusedSessionSid]);

    return (
        <SessionViewerContext.Provider value={{ portalNodesRef, getOrCreatePortalNode, removeSessionViewer }}>
            {children}

            {/* Render all session viewers into portal nodes */}
            <SessionViewerPool
                portalNodesRef={portalNodesRef}
                getOrCreatePortalNode={getOrCreatePortalNode}
            />
        </SessionViewerContext.Provider>
    );
}
